import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Receipt, Plus, Search, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSales } from '../hooks/useSales';
import { norm } from '../utils/catalog';
import { localDateStr } from '../utils/customers';
import { fmtDate, money } from '../utils/quotes';
import {
  AGING_BUCKETS, SALE_STATUS_LABELS, SALE_STATUS_TONES, balanceOf, buildCartera, carteraToCsv, downloadCsv, fromCents, isOverdue, paymentsToCsv, salesToCsv, summarize
} from '../utils/sales';

const inputCls = 'w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green';
const PAGE_SIZE = 50;

export function SaleBadge({ sale }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${SALE_STATUS_TONES[sale.status] || SALE_STATUS_TONES.por_cobrar}`}>{SALE_STATUS_LABELS[sale.status] || sale.status}</span>
      {isOverdue(sale) && <span className="text-[10px] font-bold text-orange-300">Vencida</span>}
    </span>
  );
}

const matchesStatus = (s, filter) => (filter === 'activas' ? s.status !== 'anulada' : filter === 'vencida' ? isOverdue(s) : s.status === filter);

const FILTERS = [['activas', 'Todas (sin anuladas)'], ['por_cobrar', 'Por cobrar'], ['parcial', 'Abonadas'], ['pagada', 'Pagadas'], ['vencida', 'Vencidas'], ['anulada', 'Anuladas']];

function Kpi({ label, value, tone = 'text-seryi-heading' }) {
  return (
    <div className="bg-seryi-bg border border-seryi-bg-light rounded-2xl p-4">
      <p className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">{label}</p>
      <p className={`text-lg font-black ${tone}`}>{value}</p>
    </div>
  );
}

function CarteraTab({ sales, customers, onExport }) {
  const cartera = useMemo(() => buildCartera(sales, customers), [sales, customers]);
  if (cartera.rows.length === 0) {
    return <div className="p-8 text-center text-xs text-seryi-text border border-dashed border-seryi-bg-light rounded-3xl">No hay saldos por cobrar. Las ventas con saldo aparecerán aquí por cliente y antigüedad.</div>;
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] text-seryi-text">El atraso se cuenta desde la fecha de vencimiento de cada venta (si no tiene, desde el día de la venta).</p>
        <button type="button" onClick={() => onExport(cartera)} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5 whitespace-nowrap"><Download size={13} /> Exportar cartera</button>
      </div>
      <div className="overflow-auto rounded-3xl border border-seryi-bg-light">
        <table className="w-full text-xs text-seryi-heading">
          <thead className="bg-seryi-bg-dark text-seryi-text text-[10px] uppercase">
            <tr><th className="p-3 text-left">Cliente</th><th className="p-3 text-right">Ventas</th>{AGING_BUCKETS.map((b) => <th key={b.key} className="p-3 text-right">{b.label}</th>)}<th className="p-3 text-right">Saldo total</th></tr>
          </thead>
          <tbody>
            {cartera.rows.map((r) => (
              <tr key={r.key} className="border-t border-seryi-bg-light">
                <td className="p-3 font-semibold">{r.name}{r.tax_id ? <span className="block text-[10px] text-seryi-text font-normal">NIT/CC {r.tax_id}</span> : null}</td>
                <td className="p-3 text-right">{r.count}</td>
                {AGING_BUCKETS.map((b) => <td key={b.key} className={`p-3 text-right ${b.key !== 'vigente' && r[b.key] > 0 ? 'text-orange-300 font-bold' : ''}`}>{r[b.key] ? money(fromCents(r[b.key])) : '—'}</td>)}
                <td className="p-3 text-right font-bold text-emerald-400">{money(fromCents(r.total))}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-seryi-bg-light bg-seryi-bg-dark/60 font-black">
              <td className="p-3">Total</td><td className="p-3 text-right">{cartera.totals.count}</td>
              {AGING_BUCKETS.map((b) => <td key={b.key} className="p-3 text-right">{money(fromCents(cartera.totals[b.key]))}</td>)}
              <td className="p-3 text-right text-emerald-400">{money(fromCents(cartera.totals.total))}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SalesPage() {
  const { sales, openSales, customers, quotesById, loading, tableMissing, fetchAllPayments } = useSales();
  const [tab, setTab] = useState('ventas');
  const [filter, setFilter] = useState('activas');
  const [query, setQuery] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);

  const customerById = useMemo(() => new Map(customers.map((c) => [c.id, c])), [customers]);

  const filtered = useMemo(() => sales.filter((s) => {
    if (!matchesStatus(s, filter)) return false;
    const day = localDateStr(new Date(s.created_at));
    if (from && day < from) return false;
    if (to && day > to) return false;
    const terms = norm(query).split(' ').filter(Boolean);
    if (!terms.length) return true;
    const c = customerById.get(s.customer_id);
    const hay = norm(`${s.number} ${c?.name ?? ''} ${c?.legal_name ?? ''} ${c?.phone ?? ''} ${c?.tax_id ?? ''}`);
    return terms.every((t) => hay.includes(t));
  }), [sales, filter, query, from, to, customerById]);

  const summary = useMemo(() => summarize(filtered), [filtered]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const visible = filtered.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);

  const exportSales = () => downloadCsv(`ventas_${localDateStr()}.csv`, salesToCsv(filtered, customers, quotesById));
  const exportPayments = async () => {
    const payments = await fetchAllPayments();
    if (!payments) return;
    const known = new Map([...sales, ...openSales].map((s) => [s.id, s]));
    const inRange = payments.filter((p) => { const day = localDateStr(new Date(p.paid_at)); return (!from || day >= from) && (!to || day <= to); });
    if (!inRange.length) { toast('No hay abonos en ese rango de fechas.'); return; }
    downloadCsv(`abonos_${localDateStr()}.csv`, paymentsToCsv(inRange, known, customers));
  };

  if (tableMissing) {
    return <div className="p-6 text-xs text-amber-300">Falta ejecutar la migración <code>20261003c_sales.sql</code> en Supabase para usar las ventas.</div>;
  }

  return (
    <div className="space-y-5 max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-seryi-heading flex items-center gap-2"><Receipt size={20} /> Ventas y cartera</h2>
          <p className="text-xs text-seryi-text">Ventas desde cotizaciones aceptadas o a mano, abonos y saldos por cobrar. Una venta no es una factura electrónica.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={exportSales} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5"><Download size={13} /> Exportar ventas</button>
          <button type="button" onClick={exportPayments} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5"><Download size={13} /> Exportar abonos</button>
          <Link to="/v2/ventas/nueva" className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-3 py-2 rounded-xl flex items-center gap-1.5"><Plus size={13} /> Nueva venta</Link>
        </div>
      </div>

      <div className="flex gap-2" role="tablist">
        {[['ventas', 'Ventas'], ['cartera', 'Cartera por cobrar']].map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
            className={`text-xs font-bold px-4 py-2 rounded-xl border ${tab === k ? 'bg-seryi-green/10 text-seryi-green border-seryi-green/30' : 'border-seryi-bg-light text-seryi-text'}`}>{label}</button>
        ))}
      </div>

      {tab === 'cartera' ? (
        loading ? <p className="text-xs text-seryi-text">Cargando…</p> : <CarteraTab sales={openSales} customers={customers} onExport={(c) => downloadCsv(`cartera_${localDateStr()}.csv`, carteraToCsv(c))} />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Vendido" value={money(summary.sold)} />
            <Kpi label="Cobrado" value={money(summary.collected)} tone="text-emerald-400" />
            <Kpi label="Por cobrar" value={money(summary.pending)} tone="text-amber-300" />
            <Kpi label="Vencido" value={money(summary.overdue)} tone="text-orange-300" />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-seryi-text" />
              <input className={`${inputCls} pl-8`} placeholder="Buscar por número, cliente o NIT" aria-label="Buscar ventas" value={query} onChange={(e) => { setQuery(e.target.value); setPage(0); }} />
            </div>
            <select aria-label="Filtrar por estado" className={`${inputCls} w-auto`} value={filter} onChange={(e) => { setFilter(e.target.value); setPage(0); }}>
              {FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <label className="text-[10px] uppercase font-bold text-seryi-text flex items-center gap-1.5">Desde <input type="date" aria-label="Desde" className={`${inputCls} w-auto`} value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} /></label>
            <label className="text-[10px] uppercase font-bold text-seryi-text flex items-center gap-1.5">Hasta <input type="date" aria-label="Hasta" className={`${inputCls} w-auto`} value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} /></label>
            <span className="text-[11px] text-seryi-text">{filtered.length} {filtered.length === 1 ? 'venta' : 'ventas'}</span>
          </div>

          {loading ? (
            <p className="text-xs text-seryi-text">Cargando…</p>
          ) : sales.length === 0 ? (
            <div className="p-8 text-center text-xs text-seryi-text border border-dashed border-seryi-bg-light rounded-3xl">
              Aún no hay ventas. Crea una desde una cotización aceptada o con "Nueva venta".
            </div>
          ) : (
            <div className="overflow-auto rounded-3xl border border-seryi-bg-light">
              <table className="w-full text-xs text-seryi-heading">
                <thead className="bg-seryi-bg-dark text-seryi-text text-[10px] uppercase">
                  <tr><th className="p-3 text-left">Número</th><th className="p-3 text-left">Cliente</th><th className="p-3 text-left">Fecha</th><th className="p-3 text-left">Vence</th><th className="p-3 text-left">Estado</th><th className="p-3 text-right">Total</th><th className="p-3 text-right">Pagado</th><th className="p-3 text-right">Saldo</th></tr>
                </thead>
                <tbody>
                  {visible.map((s) => {
                    const c = customerById.get(s.customer_id);
                    const bal = balanceOf(s);
                    return (
                      <tr key={s.id} className={`border-t border-seryi-bg-light hover:bg-seryi-bg-light/20 ${s.status === 'anulada' ? 'opacity-60' : ''}`}>
                        <td className="p-3 font-mono font-bold"><Link to={`/v2/ventas/${s.id}`} className="text-seryi-green hover:underline">{s.number}</Link></td>
                        <td className="p-3">{c?.legal_name || c?.name || <span className="text-seryi-text">Sin cliente</span>}</td>
                        <td className="p-3">{fmtDate(s.created_at)}</td>
                        <td className="p-3">{fmtDate(s.due_date)}</td>
                        <td className="p-3"><SaleBadge sale={s} /></td>
                        <td className="p-3 text-right">{money(s.total)}</td>
                        <td className="p-3 text-right text-emerald-400">{money(s.paid_total)}</td>
                        <td className={`p-3 text-right font-bold ${bal > 0 && s.status !== 'anulada' ? 'text-amber-300' : ''}`}>{s.status === 'anulada' ? '—' : money(bal)}</td>
                      </tr>
                    );
                  })}
                  {visible.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-seryi-text">Ninguna venta coincide con el filtro.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-3 text-xs text-seryi-text">
              <button type="button" disabled={current === 0} onClick={() => setPage(current - 1)} className="px-3 py-1 border border-seryi-bg-light rounded-lg disabled:opacity-40">Anterior</button>
              <span>Página {current + 1} de {pages}</span>
              <button type="button" disabled={current >= pages - 1} onClick={() => setPage(current + 1)} className="px-3 py-1 border border-seryi-bg-light rounded-lg disabled:opacity-40">Siguiente</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
